// Appearance controls — shared by the Properties panel (a selected element or
// a multi-selection) and the draw-mode view (the shape about to be drawn), as
// v1's draw menu was. Pure string builders plus the v1 thickness maths.
//
// Fill, stroke and thickness each have an on/off toggle (v1): off stores null
// (no fill / no stroke / a 1px hairline). Thickness is shown in m or ft, with
// a logarithmic slider from 1 cm to 10 m.

import { cap, rgbaToHex } from './html';
import { DEFAULT_FILL_COLOR, DEFAULT_STROKE_COLOR } from '../migrations';

export const SLIDER_MAX = 1000;

// v1 DrawMenu: slider position 0..1000 <-> 1..1000 cm on a log scale.
export function sliderToWidth(raw) {
  return Math.round(Math.pow(1000, raw / SLIDER_MAX) * 10) / 10;
}

export function widthToSlider(cm) {
  if (!(cm > 1)) return 0;
  return Math.min(SLIDER_MAX, Math.round((Math.log(cm) / Math.log(1000)) * SLIDER_MAX));
}

const unitFactor = (units) => (units === 'imperial' ? 30.48 : 100);
export const widthUnit = (units) => (units === 'imperial' ? 'ft' : 'm');
export function widthToDisplay(cm, units) { return (cm / unitFactor(units)).toFixed(2); }
export function widthFromDisplay(value, units) { return Math.round(value * unitFactor(units) * 10) / 10; }

// Shown (disabled) while thickness is off, and used when it's switched on.
export const DEFAULT_WIDTH = 10;

// `style`: { fillColor, strokeColor, strokeWidth, canopyStyle, opacity,
// visible } for one element or the draw tool; in a multi-selection a field is
// `undefined` where the selection disagrees (mixed).
export function appearanceHtml(style, { type, units = 'metric' } = {}) {
  if (type === 'image') return imageAppearanceHtml(style);
  const rows = [];
  if (type !== 'line') rows.push(colorRow('Fill', 'fill', style.fillColor, DEFAULT_FILL_COLOR));
  rows.push(colorRow('Stroke', 'stroke', style.strokeColor, DEFAULT_STROKE_COLOR, { toggle: type !== 'line' }));
  rows.push(thicknessRows(style.strokeWidth, units));
  if (type === 'ellipse') {
    const canopy = style.canopyStyle || 'smooth';
    rows.push(`<div class="pl2-field"><label>Canopy</label><select class="pl2-input" data-pl2="prop-canopy">${['smooth', 'lobed', 'conifer'].map((s) => `<option value="${s}" ${canopy === s ? 'selected' : ''}>${cap(s)}</option>`).join('')}</select></div>`);
  }
  return rows.join('');
}

function toggle(kind, on, mixed, label) {
  return `<input type="checkbox" class="pl2-toggle" data-pl2="${kind}" ${on ? 'checked' : ''} aria-label="${label}"${mixed ? ' title="Mixed in this selection"' : ''}>`;
}

function colorRow(label, key, value, fallback, { toggle: withToggle = true } = {}) {
  const mixed = value === undefined;
  const on = value != null;
  const picker = `<input type="color" data-pl2="prop-${key}" value="${rgbaToHex(on ? value : fallback)}"${on || !withToggle ? '' : ' disabled'}>`;
  return `<div class="pl2-field"><label>${label}</label>${withToggle ? toggle(`prop-use-${key}`, on, mixed, `Use ${label.toLowerCase()}`) : ''}${picker}${mixed ? '<span class="pl2-muted">(mixed)</span>' : ''}</div>`;
}

function thicknessRows(width, units) {
  const mixed = width === undefined;
  const on = width != null;
  const cm = on ? width : DEFAULT_WIDTH;
  const off = on ? '' : ' disabled';
  return `
    <div class="pl2-field"><label>Thickness</label>${toggle('prop-use-width', on, mixed, 'Use a set thickness')}
      <input class="pl2-input" type="number" min="0" step="0.01" data-pl2="prop-strokeWidth" value="${mixed ? '' : widthToDisplay(cm, units)}"${off}${mixed ? ' placeholder="(mixed)"' : ''}><span class="pl2-unit">${widthUnit(units)}</span></div>
    <div class="pl2-field"><label></label><input class="pl2-range pl2-range--fill" type="range" min="0" max="${SLIDER_MAX}" step="1" data-pl2="prop-strokeWidthSlider" value="${widthToSlider(cm)}" aria-label="Thickness"${off}></div>`;
}

function imageAppearanceHtml(style) {
  return `
    <div class="pl2-field"><label>Opacity</label><input class="pl2-range pl2-range--fill" type="range" min="0" max="255" step="1" data-pl2="prop-opacity" value="${style.opacity ?? 200}"></div>
    <div class="pl2-field"><label>Visible</label><input type="checkbox" data-pl2="prop-visible" ${style.visible === false ? '' : 'checked'}></div>`;
}

